/**
 * Offline demo API — the routing core, shared by both adapters.
 *
 * This module is deliberately environment-agnostic: no node imports, no DOM
 * imports. It maps a method + URL to a reply descriptor, and the adapters
 * carry that over their own transport:
 *
 *   demoApiPlugin.ts   Vite dev-server middleware (node http)
 *   installDemoApi.ts  browser `fetch` interceptor (static builds, e.g. GitHub
 *                      Pages, where there is no server to run middleware)
 *
 * Sharing the routing means the deployed demo and the dev demo cannot drift.
 *
 * Wire contracts mirrored from the frontend clients:
 *  - chat SSE:      api/commandApi.ts readSseEvents (JSON body {channel,event,data})
 *  - chart status:  api/smartApi.ts fetchSmartChartStatus ({ready,phase,resource_counts,window,detail})
 *  - step journal:  api/patientPipelineApi.ts (PipelineImportStep / continue_gate)
 *  - metadata:      services/threadService.ts ResponseMetadata
 */

/** What an adapter should send back. */
export type DemoReply =
  /** Not an API path — the adapter should let the request through untouched. */
  | { kind: 'passthrough' }
  | { kind: 'json'; status: number; body: unknown; delayMs: number }
  | { kind: 'empty'; status: number }
  /** A server-sent-event stream, as raw SSE frames. The adapter pumps the
   *  generator and stops pulling when its client disconnects. */
  | { kind: 'sse'; open: () => AsyncGenerator<string, void, unknown> };

/** Every JSON route answers through this, including its simulated latency —
 *  an instant demo reads as fake, and hides real loading states. */
function reply(body: unknown, status = 200, delayMs = 120): DemoReply {
  return { kind: 'json', status, body, delayMs };
}

/**
 * Whether this path belongs to the demo API at all.
 *
 * Careful scoping: the app's SOURCE lives in web/api/, so the dev server
 * serves real modules at /api/<file>.ts — a blanket /api/ claim would 404 the
 * app's own module graph.
 */
export function isDemoApiPath(path: string): boolean {
  return (
    path === '/health' ||
    path.startsWith('/v1/') ||
    path.startsWith('/api/v1/') ||
    path.startsWith('/api/smart/') ||
    path.startsWith('/api/exports/') ||
    // uiEventTelemetry builds its URL from the RAW env value ('same'), which
    // resolves relative to the current page — catch it wherever it lands.
    path.endsWith('/v1/telemetry/ui-events')
  );
}

import {
  DEMO_PATIENT_FHIR_ID,
  DEMO_PATIENT_NAME,
  DEMO_TENANT_ID,
  demoClinicalPreview,
  demoPatientResource,
  resourceCounts,
} from './fixtures/patient';
import { pickDemoResponse } from './fixtures/responses';
import { demoTrendAlerts, demoTrendInventory, demoTrendSeries } from './fixtures/trendViz';
// The chart surfaces answer from what dev actually returned for this patient,
// captured verbatim — see chartPayloads.generated.ts.
import {
  CARE_GAPS,
  CONDITION_PROFILE,
  FAMILY_CONSENT,
  FAMILY_CONTEXT,
  METHOD_CURVES,
  ORGAN_ALERTS,
  ORGAN_HISTORY,
  ORGAN_SCORES,
  PATIENT_SUMMARY,
  PIPELINE_STATUS,
  SDOH,
  TWIN_LIFECYCLE,
  TWIN_OVERVIEW,
} from './fixtures/chartPayloads.generated';
import { JOBS } from './fixtures/chartPayloads.generated';




const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ── demo session state (in-memory, per dev-server process) ──────────────────

/** Conversation id → the user message that opened it. */
const conversations = new Map<string, { message: string; patientId: string }>();
let conversationSeq = 0;

/** SMART session id → when its import was kicked off (staging clock). */
const importStartedAt = new Map<string, number>();
/** SMART session id → history window echoed back by status/preview. */
const importWindow = new Map<string, string>();
/**
 * True only while an import-preview request is actually being handled.
 *
 * The readiness endpoints have a race they would otherwise lose: the gate
 * fires the preview and the chart-status/pipeline-status polls together, and a
 * poll served before the preview registers its clock sees an UNKNOWN session —
 * which means "chart already loaded" (the resume case), skipping the whole
 * staged import. This flag is raised synchronously when the preview handler
 * starts, before it awaits the body, and is always cleared in the same handler.
 *
 * It is deliberately NOT keyed on the session and NOT set when the patient
 * identity is fetched: an earlier version marked sessions pending at
 * `/fhir/patient`, and any session that was then abandoned (opening the twin
 * page directly, say) left the flag raised forever — freezing pipeline-status
 * at `waiting` and showing a permanent "chart is still building" banner.
 */
let importStarting = false;


/** Organ alerts acknowledged during this demo session. */
const acknowledgedAlerts = new Set<string>();

/** The history window a trend request should answer under: the one the caller
 *  sent, else the one its SMART session actually imported. */
function sessionWindow(body: Record<string, unknown>): string | undefined {
  if (typeof body.window === 'string' && body.window) return body.window;
  const sessionId = typeof body.session_id === 'string' ? body.session_id : '';
  return importWindow.get(sessionId);
}

const IMPORT_PHASES = { importingUntil: 1400, buildingUntil: 2800 };

/** The step journal, staged off the session's import clock. done_at values are
 * ms after import start; `gating:false` rows are the background LLM products. */
const STEP_PLAN: Array<{ step_id: string; label: string; done_at: number; gating?: boolean; note?: string }> = [
  { step_id: 'fhir_fetch', label: 'FHIR chart retrieval', done_at: 900 },
  { step_id: 'fhir_import', label: 'Clinical record import', done_at: 1600 },
  { step_id: 'date_window_filter', label: 'History window filter', done_at: 1900 },
  { step_id: 'reasoning_context', label: 'Reasoning context build', done_at: 2400 },
  { step_id: 'patient_context_package', label: 'Patient context package', done_at: 3000 },
  { step_id: 'trajectory_summary', label: 'Trajectory summary', done_at: 3600, gating: false },
  { step_id: 'clinical_synthesis', label: 'Clinical synthesis', done_at: 4200, gating: false },
];

function stepsAt(elapsed: number) {
  return STEP_PLAN.map((s) => {
    const done = elapsed >= s.done_at;
    return {
      step_id: s.step_id,
      label: s.label,
      status: done ? 'done' : 'running',
      started_at: null,
      completed_at: null,
      elapsed_ms: done ? Math.max(200, Math.round(s.done_at * 0.6)) : null,
      error_short: '',
      note: s.note ?? '',
      warning: null,
      substeps: [],
      gating: s.gating !== false,
    };
  });
}

function chartElapsed(sessionId: string): number | null {
  const started = importStartedAt.get(sessionId);
  if (started == null) return null;
  return Date.now() - started;
}

/** Total length of the staged import — the point at which every step is final. */
const IMPORT_TOTAL_MS = STEP_PLAN[STEP_PLAN.length - 1].done_at;

/**
 * How far along the demo's single active import is.
 *  - a number: an import is running (or has finished) that many ms ago
 *  - 'pending': a launch is committed but the import has not started yet
 *  - null: nothing is importing — a resumed session, whose fixture chart is
 *    already "loaded"
 */
function activeImportProgress(): number | 'pending' | null {
  let newest: number | null = null;
  for (const sessionId of importStartedAt.keys()) {
    const elapsed = chartElapsed(sessionId);
    if (elapsed != null && (newest == null || elapsed < newest)) newest = elapsed;
  }
  if (newest != null) return newest;
  return importStarting ? 'pending' : null;
}


// ── SSE streams ─────────────────────────────────────────────────────────────

/**
 * One chat turn: the thinking pipeline, then the Section-1 text token by
 * token, then the authoritative metadata, then the terminator.
 */
async function* chatStream(conversationId: string): AsyncGenerator<string, void, unknown> {
  const turn = conversations.get(conversationId);
  const demo = pickDemoResponse(turn?.message ?? '');
  let eventId = 0;
  const frame = (payload: Record<string, unknown>) =>
    `id: ${++eventId}\ndata: ${JSON.stringify(payload)}\n\n`;

  // 1. thinking channel — the scripted pipeline.
  for (const step of demo.thinking) {
    await sleep(step.delay);
    yield frame({ channel: 'thinking', event: step.event, data: step.data });
  }

  // 2. final channel — stream the Section-1 text token by token.
  for (const word of demo.s1.split(/(?<=\s)/)) {
    await sleep(18);
    yield frame({ channel: 'final', event: 'stream.token', data: { token: word } });
  }

  // 3. the authoritative metadata (ResponseMetadata).
  await sleep(220);
  yield frame({
    channel: 'final',
    event: 'response.metadata',
    data: {
      conversation_id: conversationId,
      intent_type: demo.intent_type,
      tools_used: demo.tools_used,
      processing_time_ms: demo.processing_time_ms,
      confidence_score: 0.92,
      ...(demo.structured ? { structured: demo.structured } : {}),
      authoritative_final_text: demo.s1,
      suggested_next_questions: demo.suggestions,
      ...(demo.rag_sources ? { rag_sources: demo.rag_sources } : {}),
      ...(demo.extraMetadata ?? {}),
    },
  });

  await sleep(120);
  yield frame({ channel: 'final', event: 'stream.end', data: { status: 'done' } });
}

/**
 * The Job Center feed. Each new connection replays a full twin rebuild, so
 * reloading the page is enough to watch the drawer work again; a run already
 * in flight is resumed rather than restarted.
 */
async function* jobEventStream(): AsyncGenerator<string, void, unknown> {
  // The captured jobs are all finished, so there is no live progress to
  // replay. The stream stays open — closing it would flip the drawer to
  // "Live feed paused" — and simply reports nothing new.
  yield ': demo job stream connected\n\n';
  for (;;) {
    await sleep(25000);
    yield ': ping\n\n';
  }
}

export async function routeDemoRequest(
  method: string,
  url: URL,
  readBody: () => Promise<Record<string, unknown>>,
): Promise<DemoReply> {
  const path = url.pathname;

  if (!isDemoApiPath(path)) return { kind: 'passthrough' as const };

  // ── health ────────────────────────────────────────────────────────────────
  if (path === '/health' || path === '/v1/health') {
    return reply({ status: 'ok', service: 'march-demo-fixtures', version: 'demo' }, 200, 40);
  }

  // ── telemetry (fire-and-forget in the app) ───────────────────────────────
  if (path.endsWith('/v1/telemetry/ui-events')) {
    return { kind: 'empty' as const, status: 204 };
  }

  // ── SMART session ────────────────────────────────────────────────────────
  if (path === '/api/smart/fhir/patient' && method === 'POST') {
    await readBody();
    return reply(demoPatientResource, 200, 260);
  }

  if (path === '/api/smart/import/preview' && method === 'POST') {
    // Raised BEFORE the first await so a concurrent readiness poll cannot see
    // this session as "never imported" and skip the staged import.
    importStarting = true;
    let body: Record<string, unknown>;
    try {
      body = await readBody();
    } catch {
      importStarting = false;
      throw new Error('unreadable preview body');
    }
    const sessionId = String(body.session_id ?? 'demo');
    const window = typeof body.window === 'string' ? body.window : '2y';
    // Every preview call restarts the staging clock: a fresh launch replays
    // the full importing → building → ready arc. The recovered-resume path
    // never calls preview when the chart is already ready, so it stays
    // instant — matching the real reuse behavior.
    importStartedAt.set(sessionId, Date.now());
    importWindow.set(sessionId, window);
    importStarting = false;
    // The real endpoint IS the import and takes a while; hold the response
    // until just past chart-ready so the status poll drives the gate.
    const elapsed = chartElapsed(sessionId) ?? 0;
    await sleep(Math.max(0, IMPORT_PHASES.buildingUntil + 400 - elapsed));
    return reply({ data_window: window, ...demoClinicalPreview }, 200, 60);
  }

  if (path === '/api/smart/session/chart-status' && method === 'POST') {
    const body = await readBody();
    const sessionId = String(body.session_id ?? 'demo');
    const window = importWindow.get(sessionId) ?? (typeof body.window === 'string' ? body.window : '2y');
    // This session's own clock when it has one; otherwise the demo's single
    // active import (covers the poll that arrives before preview registers).
    const own = chartElapsed(sessionId);
    const progress = own != null ? own : activeImportProgress();
    const counts: Record<string, number> = {};
    for (const rc of resourceCounts) counts[rc.resourceType] = rc.count;
    if (progress === 'pending') {
      return reply({ ready: false, phase: 'importing', resource_counts: {}, window, detail: 'Getting the record from the demo store…' }, 200, 90);
    }
    if (progress == null || progress >= IMPORT_PHASES.buildingUntil) {
      // Nothing importing (resume path) or the import finished: chart is loaded.
      return reply({ ready: true, phase: 'ready', resource_counts: counts, window, detail: 'Chart loaded from demo fixtures.' }, 200, 90);
    }
    if (progress < IMPORT_PHASES.importingUntil) {
      return reply({ ready: false, phase: 'importing', resource_counts: {}, window, detail: 'Getting the record from the demo store…' }, 200, 90);
    }
    return reply({ ready: false, phase: 'building', resource_counts: counts, window, detail: 'Preparing the demo chart…' }, 200, 90);
  }

  if (path === '/api/smart/session/close' && method === 'POST') {
    return reply({ status: 'closed' }, 200, 60);
  }

  // ── step journal / pipeline status ───────────────────────────────────────
  const pipelineMatch = path.match(/^\/v1\/patient\/([^/]+)\/pipeline-status$/);
  if (pipelineMatch && method === 'GET') {
    const progress = activeImportProgress();
    // 'pending' means the launch is committed but the import has not started —
    // every step is still ahead, so the checklist must show them running.
    const elapsed = progress === 'pending' ? 0 : progress;
    const finishedAll = elapsed == null || elapsed >= IMPORT_TOTAL_MS;
    const steps = stepsAt(elapsed ?? Number.MAX_SAFE_INTEGER);
    const allFinal = steps.every((s) => s.status !== 'running');
    return reply({
      patient_id: pipelineMatch[1],
      stage: finishedAll ? 'ready' : 'building_twin',
      ready: finishedAll,
      twin: { ready: finishedAll },
      organs: { ready: elapsed == null || elapsed >= 2400 },
      summary: { ready: finishedAll },
      trajectory: { available: true, ready: finishedAll },
      last_refresh: new Date().toISOString(),
      steps,
      continue_gate: allFinal ? 'ready' : 'waiting',
    }, 200, 80);
  }

  // ── chat: submit / stream / active / cancel ──────────────────────────────
  if (path === '/api/v1/chat/message' && method === 'POST') {
    const body = await readBody();
    const conversationId =
      typeof body.conversation_id === 'string' && body.conversation_id.trim()
        ? body.conversation_id.trim()
        : `demo-conv-${++conversationSeq}`;
    conversations.set(conversationId, {
      message: String(body.message ?? ''),
      patientId: String(body.patient_id ?? DEMO_PATIENT_FHIR_ID),
    });
    return reply({ conversation_id: conversationId }, 200, 180);
  }

  if (path === '/api/v1/chat/active' && method === 'GET') {
    return reply({ active: false, pending: false }, 200, 60);
  }

  if (path === '/api/v1/chat/cancel' && method === 'POST') {
    const conversationId = url.searchParams.get('conversation_id') || '';
    return reply({ status: 'cancelled', conversation_id: conversationId }, 200, 60);
  }

  if (path === '/api/v1/chat/stream' && method === 'GET') {
    return { kind: 'sse' as const, open: () => chatStream(url.searchParams.get('conversation_id') || '') };
  }

  // ── conversations (server-side thread sync — demo keeps threads local) ───
  if (path === '/v1/conversations' && method === 'GET') {
    return reply({ conversations: [] }, 200, 90);
  }
  const turnsMatch = path.match(/^\/v1\/conversations\/([^/]+)\/turns$/);
  if (turnsMatch && method === 'GET') {
    return reply({ turns: [] }, 200, 90);
  }

  // ── jobs (Job Center mounts on every route) ──────────────────────────────
  if (path === '/v1/jobs' && method === 'GET') {
    return reply(JOBS, 200, 80);
  }
  if (path === '/v1/jobs/seen' && method === 'POST') {
    const body = await readBody();
    const ids = Array.isArray(body.job_ids) ? (body.job_ids as string[]) : [];
    // The client reads `marked`, not `ok` — an acknowledged job must come back
    // named or the red badge never clears.
    return reply({ marked: ids }, 200, 40);
  }
  if (path === '/v1/jobs/stream' && method === 'GET') {
    return { kind: 'sse' as const, open: () => jobEventStream() };
  }

  // NB: must come after the reserved /v1/jobs/* routes above — this pattern
  // also matches `/v1/jobs/stream`, and matching it here would 404 the stream.
  const jobDetail = path.match(/^\/v1\/jobs\/([^/]+)$/);
  if (jobDetail && method === 'GET') {
    const jobId = decodeURIComponent(jobDetail[1]);
    const all = (JOBS as unknown as { jobs?: Array<Record<string, unknown>> }).jobs ?? [];
    const found = all.find((j) => String(j.job_id) === jobId);
    if (!found) return reply({ detail: 'Job not found in the offline demo.' }, 404, 40);
    // Per-job event history was not captured, so the timeline is empty rather
    // than filled with invented transitions.
    return reply({ job: found, events: [] }, 200, 70);
  }
  const jobCancel = path.match(/^\/v1\/jobs\/([^/]+)\/cancel$/);
  if (jobCancel && method === 'POST') {
    return reply({ accepted: true, job_id: decodeURIComponent(jobCancel[1]) }, 200, 60);
  }
  const jobRetry = path.match(/^\/v1\/jobs\/([^/]+)\/retry$/);
  if (jobRetry && method === 'POST') {
    const from = decodeURIComponent(jobRetry[1]);
    return reply({ accepted: true, job_id: `${from}-retry`, retried_from: from }, 200, 60);
  }
  // ── patient lists ────────────────────────────────────────────────────────
  if (path === '/v1/patients' && method === 'GET') {
    return reply([
      { mrn: DEMO_PATIENT_FHIR_ID, name: DEMO_PATIENT_NAME, status: 'Active', risk: 'Moderate' },
    ], 200, 140);
  }
  if (path === '/v1/admin/smart-patients' && method === 'GET') {
    return reply({
      tenant_id: DEMO_TENANT_ID,
      count: 1,
      patients: [{
        patient_id: DEMO_PATIENT_FHIR_ID,
        full_name: DEMO_PATIENT_NAME,
        given_names: 'Alicia Renee',
        family_name: 'Ward',
        active: true,
        date_of_birth: '1987-10-02',
        sex: 'female',
        source: 'demo-fixture',
        created_at: '2026-08-01T09:00:00Z',
        updated_at: '2026-08-20T09:00:00Z',
        sync_status: 'ready',
      }],
    }, 200, 120);
  }

  // ── workspace entity lists (bare fetch, raw arrays) ──────────────────────
  const entityList = path.match(/^\/v1\/(assessments|alerts|redflags|exports|simulations|visits)$/);
  if (entityList && method === 'GET') {
    const kind = entityList[1];
    const items: Record<string, Array<Record<string, unknown>>> = {
      assessments: [
        { id: 'as-1', date: '2026-07-29', time: '10:40', type: 'assessment', title: 'Diabetes follow-up assessment', summary: 'A1c at goal; kidney protection is the open thread.', chips: ['diabetes', 'ckd'] },
      ],
      alerts: [
        { id: 'al-1', date: '2026-02-04', time: '09:15', type: 'alert', title: 'Albuminuria above threshold', summary: 'Urine ACR 42 mg/g — repeat sample recommended.', chips: ['kidney'] },
      ],
      redflags: [],
      exports: [],
      simulations: [],
      visits: [
        { id: 'vi-1', date: '2026-07-29', time: '10:30', type: 'visit', title: 'Office visit — diabetes follow-up', summary: 'Reviewed home BP log and renewed prescriptions.', chips: ['ambulatory'] },
        { id: 'vi-2', date: '2026-05-13', time: '14:00', type: 'visit', title: 'Office visit — hypertension check', summary: 'BP improving on current regimen.', chips: ['ambulatory'] },
      ],
    };
    return reply(items[kind] ?? [], 200, 110);
  }

  // ── digital twin (chat drawer panel) ─────────────────────────────────────
  const twinOverview = path.match(/^\/api\/v1\/digital-twin\/patient\/([^/]+)\/overview$/);
  if (twinOverview && method === 'GET') {
    return reply({
      available: true,
      summary: 'Demo twin: cardiometabolic cluster with improving control; kidney axis is the active risk.',
      patient: {
        tenant_id: DEMO_TENANT_ID,
        entity_type: 'patient',
        entity_id: twinOverview[1],
        snapshot_ts: '2026-08-20T09:00:00Z',
        effective_ts: '2026-08-20T09:00:00Z',
        state: { a1c: 6.9, egfr: 54, bp: '134/82', acr: 42 },
        metadata: { source_component: 'demo-fixture' },
      },
      risk: null,
      workflow: null,
      recommendation: null,
      snapshot_counts: { patient: 1 },
    }, 200, 160);
  }
  const twinReplay = path.match(/^\/api\/v1\/digital-twin\/patient\/([^/]+)\/replay$/);
  if (twinReplay && method === 'GET') {
    return reply({ snapshots: [] }, 200, 120);
  }

  // ── digital twin core (narratives, lifecycle) ────────────────────────────
  const twinCoreOverview = path.match(/^\/v1\/digital-twin-core\/([^/]+)\/overview$/);
  if (twinCoreOverview && method === 'GET') {
    return reply(TWIN_OVERVIEW, 200, 220);
  }
  const twinLifecycle = path.match(/^\/v1\/digital-twin-core\/([^/]+)\/lifecycle$/);
  if (twinLifecycle && method === 'GET') {
    return reply(TWIN_LIFECYCLE, 200, 160);
  }
  const twinFeedback = path.match(/^\/v1\/digital-twin-core\/([^/]+)\/summary-feedback$/);
  if (twinFeedback && method === 'POST') {
    await readBody();
    return reply({ ok: true, feedback_id: 'demo-feedback-1' }, 200, 90);
  }

  // ── organ health ─────────────────────────────────────────────────────────
  const organHistory = path.match(/^\/v1\/clinical\/organ-scores\/([^/]+)\/history$/);
  if (organHistory && method === 'GET') {
    return reply((ORGAN_HISTORY[url.searchParams.get('organ') || 'overall'] ?? { history: [] }), 200, 120);
  }
  const organScores = path.match(/^\/v1\/clinical\/organ-scores\/([^/]+)$/);
  if (organScores && method === 'GET') {
    return reply(ORGAN_SCORES, 200, 200);
  }
  const alertAck = path.match(/^\/v1\/clinical\/organ-alerts\/([^/]+)\/([^/]+)\/acknowledge$/);
  if (alertAck && method === 'POST') {
    acknowledgedAlerts.add(decodeURIComponent(alertAck[2]));
    return { kind: 'empty' as const, status: 204 };
  }
  const organAlerts = path.match(/^\/v1\/clinical\/organ-alerts\/([^/]+)$/);
  if (organAlerts && method === 'GET') {
    const body = ORGAN_ALERTS as unknown as { alerts: Array<Record<string, unknown>> };
    return reply({
      alerts: (body.alerts ?? []).map((a: Record<string, unknown>) =>
        acknowledgedAlerts.has(String(a.alert_id))
          ? { ...a, acknowledged_at: new Date().toISOString(), acknowledged_by: 'demo.clinician@example.org' }
          : a,
      ),
    }, 200, 150);
  }
  if (path === '/v1/clinical/organ-health/method-curves' && method === 'GET') {
    return reply(METHOD_CURVES, 200, 110);
  }

  // ── social context, family, consent, prepared summary ────────────────────
  const sdohScreening = path.match(/^\/v1\/clinical\/sdoh\/([^/]+)\/screening$/);
  if (sdohScreening && method === 'POST') {
    const body = await readBody();
    return reply({ ...SDOH, recorded: true, sdoh_id: `demo-sdoh-${String(body.domain ?? 'new')}` }, 200, 200);
  }
  const sdoh = path.match(/^\/v1\/clinical\/sdoh\/([^/]+)$/);
  if (sdoh && method === 'GET') {
    return reply(SDOH, 200, 180);
  }
  const familyContext = path.match(/^\/v1\/clinical\/family-context\/([^/]+)$/);
  if (familyContext && method === 'GET') {
    return reply(FAMILY_CONTEXT, 200, 170);
  }
  const consentRevoke = path.match(/^\/v1\/clinical\/family-consent\/([^/]+)\/([^/]+)\/revoke$/);
  if (consentRevoke && method === 'POST') {
    return reply(FAMILY_CONSENT, 200, 140);
  }
  const familyConsent = path.match(/^\/v1\/clinical\/family-consent\/([^/]+)$/);
  if (familyConsent && (method === 'GET' || method === 'POST')) {
    if (method === 'POST') await readBody();
    return reply(FAMILY_CONSENT, 200, 140);
  }
  const patientSummary = path.match(/^\/v1\/clinical\/patient-summary\/([^/]+)$/);
  if (patientSummary && method === 'GET') {
    return reply(PATIENT_SUMMARY, 200, 160);
  }
  const careGaps = path.match(/^\/v1\/clinical\/care-gaps\/([^/]+)$/);
  if (careGaps && method === 'GET') {
    return reply(CARE_GAPS, 200, 150);
  }
  const conditionProfile = path.match(/^\/v1\/clinical\/condition-profile\/([^/]+)$/);
  if (conditionProfile && method === 'GET') {
    return reply(CONDITION_PROFILE, 200, 190);
  }

  // ── trend visualization ──────────────────────────────────────────────────
  if (path === '/api/smart/session/trend-viz/inventory' && method === 'POST') {
    const body = await readBody();
    return reply(demoTrendInventory(sessionWindow(body)), 200, 240);
  }
  if (path === '/api/smart/session/trend-viz/series' && method === 'POST') {
    const body = await readBody();
    const itemIds = Array.isArray(body.item_ids) ? (body.item_ids as string[]) : [];
    return reply(demoTrendSeries(itemIds, sessionWindow(body)), 200, 260);
  }
  if (path === '/api/smart/session/trend-viz/alerts' && method === 'POST') {
    const body = await readBody();
    return reply(demoTrendAlerts(sessionWindow(body)), 200, 200);
  }

  // ── everything else under the API prefixes: honest 404 ───────────────────
  // Clients treat 404 as "endpoint absent" and degrade to their unavailable
  // states — the truthful demo behavior for surfaces this mode does not cover
  // (admin consoles, organ scores, SDOH, digital-twin-core, IAM).
  return reply({ detail: 'Not available in the offline demo.' }, 404, 60);
}
